import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Headphones, Sparkles, ChevronRight, Search } from "lucide-react";
import BrandNav from "../components/BrandNav";

/**
 * MeetIndex — public landing page listing every historical figure who has
 * a two-minute audio drama available. Solves the reported bug where
 * navigating to /meet (no slug) fell through the catch-all Route and
 * redirected to `/` (the landing "coverpage").
 *
 * Data source: GET /api/portraits (public, returns 20 figures + availability).
 * Each card links to /meet/:slug which is the existing drama player page.
 */

// Era snippets to give each card a little historical anchor. Keeps the
// component self-contained (no extra backend field needed) and mirrors the
// front-matter era we already put in each script markdown.
const ERAS = {
  ada_lovelace:          "Victorian London \u00b7 1815\u20131852",
  grace_hopper:          "US Navy \u00b7 1906\u20131992",
  florence_nightingale:  "Crimean War \u00b7 1820\u20131910",
  mary_jackson:          "NASA Langley \u00b7 1921\u20132005",
  maria_sibylla_merian:  "Germany & Suriname \u00b7 1647\u20131717",
  sophie_germain:        "Revolutionary Paris \u00b7 1776\u20131831",
  timnit_gebru:          "Ethiopia \u2192 USA \u00b7 present day",
  cynthia_rudin:         "United States \u00b7 present day",
  katherine_johnson:     "NASA Langley \u00b7 1918\u20132020",
  eliza:                 "MIT computer lab \u00b7 1966",
  alan_turing:           "England \u00b7 1912\u20131954",
  m_c_escher:            "Netherlands \u00b7 1898\u20131972",
  donald_knuth:          "United States \u00b7 present day",
  tim_berners_lee:       "England & CERN \u00b7 1955\u2013present",
  alexander_graham_bell: "Scotland \u2192 USA \u00b7 1847\u20131922",
  john_tukey:            "Bell Labs \u00b7 1915\u20132000",
  thomas_bayes:          "Kent, England \u00b7 1701\u20131761",
  frank_rosenblatt:      "Cornell \u00b7 1928\u20131971",
  claude_shannon:        "Bell Labs & MIT \u00b7 1916\u20132001",
  norbert_wiener:        "United States \u00b7 1894\u20131964",
};

const TAGLINES = {
  ada_lovelace:          "The first person to write a computer program \u2014 a century before computers existed.",
  grace_hopper:          "The admiral who taught machines to understand English.",
  florence_nightingale:  "The lamp-carrying nurse who invented data visualisation.",
  mary_jackson:          "NASA's first Black woman engineer.",
  maria_sibylla_merian:  "A 17th-century artist who sailed alone to a jungle to paint butterflies.",
  sophie_germain:        "Studied mathematics in secret under a false name.",
  timnit_gebru:          "The scientist making AI fair for everyone.",
  cynthia_rudin:         "The prize-winning voice for AI that humans can understand.",
  katherine_johnson:     "The human computer whose pencil sent men to the Moon.",
  eliza:                 "The very first chatbot \u2014 and the people who mistook her for real.",
  alan_turing:           "Broke Nazi codes and imagined the modern computer.",
  m_c_escher:            "Drew impossible staircases and infinite worlds.",
  donald_knuth:          "Wrote the encyclopaedia of algorithms \u2014 and pays cash for typos.",
  tim_berners_lee:       "Invented the World Wide Web and gave it away for free.",
  alexander_graham_bell: "The teacher of the deaf who invented the telephone by accident.",
  john_tukey:            "Coined the words 'bit' and 'software'.",
  thomas_bayes:          "A minister whose lost formula now powers spam filters.",
  frank_rosenblatt:      "Built the first machine that could learn.",
  claude_shannon:        "The juggling unicycle rider who invented information theory.",
  norbert_wiener:        "The child prodigy who named the science of feedback.",
};

export default function MeetIndex() {
  const [state, setState] = useState({ loading: true, portraits: [], error: null });
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/portraits`)
      .then((r) => r.json())
      .then((body) => {
        if (!alive) return;
        setState({ loading: false, portraits: body.portraits || [], error: null });
      })
      .catch(() => {
        if (alive) setState({ loading: false, portraits: [], error: "Could not load the heroes list." });
      });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const list = state.portraits.filter((p) => p.available);
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((p) =>
      p.name.toLowerCase().includes(needle) ||
      (TAGLINES[p.slug] || "").toLowerCase().includes(needle) ||
      (ERAS[p.slug] || "").toLowerCase().includes(needle)
    );
  }, [state.portraits, q]);

  return (
    <div data-testid="meet-index-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <section className="ftg-meet-index__hero">
          <div className="ftg-meet-index__eyebrow">
            <Sparkles size={14} /> Meet the Heroes
          </div>
          <h1 className="ftg-meet-index__title">
            20 dramatised audio biographies.
            <span className="ftg-meet-index__title-thin"> Two minutes each. Warm narration. Gentle music.</span>
          </h1>
          <p className="ftg-meet-index__lede">
            From Ada Lovelace dreaming up computer programs a century early, to Timnit Gebru
            making AI fair for everyone &mdash; these are the curious minds behind our chapter books.
          </p>
          <div className="ftg-meet-index__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, era, or story&hellip;"
              aria-label="Search heroes"
              data-testid="meet-index-search"
            />
          </div>
        </section>

        {state.loading && (
          <div className="ftg-loader" data-testid="meet-index-loading">
            <div className="ftg-loader__dot" />
          </div>
        )}

        {!state.loading && state.error && (
          <div className="ftg-verify__card ftg-verify__card--invalid" data-testid="meet-index-error">
            <h2>Couldn&rsquo;t load heroes</h2>
            <p>{state.error}</p>
          </div>
        )}

        {!state.loading && !state.error && (
          <>
            <div className="ftg-meet-index__count" data-testid="meet-index-count">
              Showing {filtered.length} of {state.portraits.filter((p) => p.available).length} heroes
            </div>

            <ul className="ftg-meet-index__grid" data-testid="meet-index-grid">
              {filtered.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/meet/${p.slug}`}
                    className="ftg-meet-card"
                    data-testid={`meet-index-card-${p.slug}`}
                  >
                    <div className="ftg-meet-card__gender-mark" aria-hidden="true">
                      {p.voice_gender === "female" ? "\u2640" : "\u2642"}
                    </div>
                    <div className="ftg-meet-card__body">
                      <div className="ftg-meet-card__name">{p.name}</div>
                      <div className="ftg-meet-card__era">{ERAS[p.slug] || ""}</div>
                      <div className="ftg-meet-card__tag">{TAGLINES[p.slug] || ""}</div>
                    </div>
                    <div className="ftg-meet-card__cta" aria-hidden="true">
                      <Headphones size={14} /> Listen <ChevronRight size={14} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {filtered.length === 0 && (
              <div className="ftg-meet-index__empty" data-testid="meet-index-empty">
                No heroes matched &ldquo;{q}&rdquo;. Try &ldquo;NASA&rdquo;, &ldquo;chatbot&rdquo;, or a name.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
